import { useMemo, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";
import { EventNotice } from "../types/event";

// S2 endpoint — notice admin search
const NOTICE_END_POINT = "notice/admin/";

/** Shorter than this is not worth a round trip — matches federated search. */
export const MIN_NOTICE_QUERY_LENGTH = 2;

/**
 * Notice title search, driven as a typeahead.
 *
 * Held by React Query rather than fired by a Search button, because a typeahead
 * asks per keystroke: the cache collapses the repeats (backspacing to a term
 * already typed costs nothing), and keying on the term is what makes a slow
 * answer for "swim" unable to land on top of the results for "swimming".
 *
 * `keepPreviousData` keeps the dropdown showing the last matches while the next
 * request runs, so the list narrows as someone types instead of blanking
 * between every letter.
 */
export function useNoticeManager() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const [term, setTerm] = useState("");

	const trimmed = term.trim();
	const longEnough = trimmed.length >= MIN_NOTICE_QUERY_LENGTH;

	const query = useQuery({
		queryKey: ["notice-search", trimmed],
		queryFn: async (): Promise<EventNotice[]> => {
			const res = await callPymbleApi(
				instance,
				"GET",
				`${NOTICE_END_POINT}?title=${encodeURIComponent(trimmed)}`
			);
			if (!res) return [];

			// The endpoint answers either a bare list or a single-element envelope
			// with the rows under `data`.
			const items: EventNotice[] =
				Array.isArray(res) && res[0]?.data ? res[0].data : res;
			if (!Array.isArray(items)) return [];

			// Sorted by title, and tolerant of a notice that has none — the old
			// unguarded localeCompare threw on one and lost the whole search.
			return [...items]
				.filter((n) => n?._id)
				.sort((a, b) => (a.Title ?? "").localeCompare(b.Title ?? ""));
		},
		enabled: isAuthenticated && longEnough,
		placeholderData: keepPreviousData,
	});

	return {
		term,
		setTerm,
		longEnough,
		minQueryLength: MIN_NOTICE_QUERY_LENGTH,
		notices: useMemo(() => query.data ?? [], [query.data]),
		searching: longEnough && query.isFetching,
		searchFailed: !!query.error,
	};
}
